import { ElementRef, MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { useDraggable } from "react-use-draggable-scroll";
import classNames from "classnames";
import { DateTime } from "luxon";
import { Broadcast } from "~/api/broadcasts/shared/schemas";
import {
  TournamentScheduleDate,
  TournamentScheduleMatch,
} from "~/shared/helpers/tournaments/mapBracketsToTournamentSchedule";
import { MatchRow } from "./components/MatchRow";
import { GameId } from "@portal-frontend-ssr/providers";
import { ClientOnly } from "remix-utils/client-only";

export interface TournamentScheduleProps {
  dates: TournamentScheduleDate[];
  broadcasts?: Broadcast[];
  tournamentName: string;
  linkToMatchPage?: boolean;
  linkToTeamPage?: boolean;
  gameId: GameId;
}

const getClosestScheduleDate = (dates: TournamentScheduleDate[]) => {
  if (dates.length === 0) return null;

  const today = DateTime.now().startOf("day");

  return [...dates.map((scheduleDate) => scheduleDate.date)].sort((a, b) => {
    const aDiff = Math.abs(today.toMillis() - a.toMillis());
    const bDiff = Math.abs(today.toMillis() - b.toMillis());
    return aDiff - bDiff;
  })[0];
};

const TournamentSchedule = ({ dates, tournamentName, gameId }: TournamentScheduleProps) => {
  const [activeDate, setActiveDate] = useState<DateTime | null>(() => getClosestScheduleDate(dates));

  useEffect(() => {
    setActiveDate(getClosestScheduleDate(dates));
  }, [dates]);

  const matchesForSelectedDate = useMemo(() => {
    if (!activeDate) {
      return [];
    }

    // We want to show all matches (for a given date) IF and only when at least any on match (across all stages) has two valid teams + a scheduled time.
    const allDates = dates.flat();
    const hasValidMatch = allDates.some((scheduleDate) => {
      return scheduleDate.matches.some((match) => match.teamA !== null && match.teamB !== null && match.scheduledAt);
    });

    const matches = allDates.find((scheduleDate) => scheduleDate.date.equals(activeDate))?.matches ?? [];

    const dedupedMatches = matches.reduce<Record<string, TournamentScheduleMatch>>((acc, match) => {
      acc[match.uuid] = match;
      return acc;
    }, {});

    return hasValidMatch ? Object.values(dedupedMatches) : [];
  }, [activeDate, dates]);

  return (
    <div className="w-full overflow-x-clip">
      {matchesForSelectedDate.length > 0 && (
        <DateSelector dates={dates} activeDate={activeDate} setActiveDate={setActiveDate} />
      )}
      <div className="mt-1 flex flex-col gap-1">
        {matchesForSelectedDate.length === 0 && (
          <p className="mb-2 flex h-10 items-center justify-center rounded-small bg-background-95 px-2 text-center text-white font-style-label-l3 md:h-16 md:px-4">
            Match data coming soon
          </p>
        )}
        <ClientOnly>
          {() =>
            matchesForSelectedDate.map((match) => (
              <MatchRow key={match.uuid} match={match} tournamentName={tournamentName} gameId={gameId} />
            ))
          }
        </ClientOnly>
      </div>
    </div>
  );
};

const DateSelector = ({
  dates,
  activeDate,
  setActiveDate,
}: {
  dates: TournamentScheduleDate[];
  activeDate: DateTime | null;
  setActiveDate: (date: DateTime) => void;
}) => {
  const tabsRef = useRef<ElementRef<"div">>(null);
  const { events } = useDraggable(tabsRef as MutableRefObject<ElementRef<"div">>);

  // Scrolls to the end of the tabs if the selected tab is the last one
  useEffect(() => {
    if (!tabsRef.current) return;
    if (!activeDate) return;

    if (activeDate.toUnixInteger() === dates[dates.length - 1].date.toUnixInteger()) {
      tabsRef.current.scroll({ behavior: "smooth", left: tabsRef.current.scrollWidth });
    }
  }, [activeDate, dates]);

  return (
    <div ref={tabsRef} {...events} className="mb-4 flex gap-1 overflow-y-auto scrollbar-none">
      {dates.map((scheduleDate) => (
        <ClientOnly
          key={scheduleDate.date.toISO()}
          fallback={
            <div className="flex h-9 place-items-center rounded-full border border-background-80 bg-background-100 px-4 sm:px-6">
              <div className="h-3 w-16 animate-pulse rounded-full bg-background-80" />
            </div>
          }
        >
          {() => (
            <button
              onClick={() => setActiveDate(scheduleDate.date)}
              disabled={activeDate != null ? scheduleDate.date.equals(activeDate) : undefined}
              className={classNames(
                "h-9 !cursor-pointer place-items-center whitespace-nowrap rounded-full px-4 transition-all font-style-body-b3 disabled:!cursor-default sm:px-6",
                "border border-background-80 bg-background-100 text-neutral hover:border-neutral",
                "disabled:border-foreground-100 disabled:bg-background-90 disabled:!text-white",
              )}
            >
              {scheduleDate.label}
            </button>
          )}
        </ClientOnly>
      ))}
    </div>
  );
};

export { TournamentSchedule };
